import { Injectable } from '@angular/core';
import { Mesh, Vector3 } from 'three';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { Axis } from '../../renderers/side-view/side-view.component';
import { CuboidHelper } from '../mesh/cuboid/cuboid-helper';
import {
  AbstractShortcutTransformService,
  getShortcutNameWithoutTransformModifier,
  shouldDoSmallTransform,
} from './abstract-shortcut-transform.service';
import { resizeShortcuts, TranslationShortcut } from './transform-shortcuts';

@Injectable()
export class ShortcutResizeService extends AbstractShortcutTransformService {
  constructor() {
    super(resizeShortcuts);
  }

  // eslint-disable-next-line max-lines-per-function,complexity -- we need two cases per X, Y, Z axis
  protected transformCuboid(
    cuboid: CuboidHelper,
    shortcut: TranslationShortcut,
    sourceOutput: AnnotationOutput,
  ) {
    const stepSize = shouldDoSmallTransform(shortcut) ? 0.5 : 1;
    let resizedBox: Mesh;
    switch (getShortcutNameWithoutTransformModifier(shortcut)) {
      case 'contractDown':
        resizedBox = resizeCuboid(cuboid, 1, -stepSize);
        break;
      case 'contractLeft':
        resizedBox = resizeCuboid(cuboid, 0, -stepSize);
        break;
      case 'contractHeight':
        resizedBox = resizeCuboid(cuboid, 2, -stepSize);
        break;
      case 'expandRight':
        resizedBox = resizeCuboid(cuboid, 0, stepSize);
        break;
      case 'expandUp':
        resizedBox = resizeCuboid(cuboid, 1, stepSize);
        break;
      case 'expandHeight':
        resizedBox = resizeCuboid(cuboid, 2, stepSize);
        break;
      default:
        throw new Error(`Unknown shortcut: ${shortcut}`);
    }

    this.store.updateFocusedLocationFromCuboidMesh(resizedBox);
    this.trackTransform(cuboid, shortcut, 'resize', sourceOutput);
  }
}

function resizeCuboid(cuboid: CuboidHelper, axis: Axis, factor: number) {
  const scaleAmount = 0.01;
  const currentScale = cuboid.box.scale.getComponent(axis);

  const axisArray = [0, 0, 0];
  axisArray[axis] = 1;
  const axisVector = new Vector3().fromArray(axisArray);

  cuboid.box.scale.setComponent(axis, currentScale + scaleAmount * factor);

  if (axis === 2) {
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    factor *= -1;
  }

  cuboid.box.translateOnAxis(axisVector, scaleAmount * -0.5 * factor);

  return cuboid.box.clone();
}
